/**
 * 玩家回放 - 纯事件列表 (替代端点 /events)
 *
 * 仅展示 player_events, 用 offset 分页。
 */

import React, { useCallback, useEffect, useState } from 'react'
import {
  Card,
  Table,
  Tag,
  Pagination,
  message,
  Empty,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'

import { playbackApi } from '@/services/api'
import type { PlaybackEventItem } from '@/types/playback'

interface Props {
  worldId: string
  playerId: string
}

const PAGE_SIZE = 50

const EventsList: React.FC<Props> = ({ worldId, playerId }) => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<PlaybackEventItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)

  const fetchEvents = useCallback(async () => {
    setLoading(true)
    try {
      const response = await playbackApi.getEvents(worldId, playerId, {
        page,
        page_size: PAGE_SIZE,
      })
      setItems(response.data?.items ?? [])
      setTotal(response.data?.total ?? 0)
    } catch (error: any) {
      message.error(error?.message ?? '加载事件失败')
      console.error('fetchEvents error:', error)
    } finally {
      setLoading(false)
    }
  }, [worldId, playerId, page])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const columns: ColumnsType<PlaybackEventItem> = [
    {
      title: '时间',
      dataIndex: 'occurred_at',
      key: 'occurred_at',
      width: 150,
      render: (val: string) => dayjs(val).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '分类',
      dataIndex: 'event_category',
      key: 'event_category',
      width: 100,
      render: (val: string) => <Tag>{val}</Tag>,
    },
    {
      title: '类型',
      dataIndex: 'event_type',
      key: 'event_type',
      width: 200,
    },
    {
      title: 'Actor',
      key: 'actor',
      width: 160,
      render: (_, record) =>
        record.actor_type ? `${record.actor_type}: ${record.actor_id ?? '-'}` : '-',
    },
    {
      title: 'Request ID',
      dataIndex: 'request_id',
      key: 'request_id',
      width: 120,
      render: (val: string | null) =>
        val ? <code className="text-xs">{val.slice(0, 12)}</code> : '-',
    },
    {
      title: 'Data',
      dataIndex: 'event_data',
      key: 'event_data',
      render: (val: Record<string, unknown> | null) =>
        val ? (
          <code className="text-xs" style={{ wordBreak: 'break-all' }}>
            {JSON.stringify(val).slice(0, 200)}
          </code>
        ) : (
          '-'
        ),
    },
  ]

  return (
    <Card title="事件列表" size="small">
      {items.length === 0 && !loading ? (
        <Empty description="暂无事件" />
      ) : (
        <>
          <Table<PlaybackEventItem>
            rowKey="id"
            columns={columns}
            dataSource={items}
            loading={loading}
            pagination={false}
            size="small"
            scroll={{ x: 800 }}
          />
          <div className="text-right mt-4">
            <Pagination
              current={page}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={setPage}
              showSizeChanger={false}
            />
          </div>
        </>
      )}
    </Card>
  )
}

export default EventsList